import { ChangeDetectionStrategy, Component, computed, DestroyRef, effect, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import { UsersListComponent } from '@app/components/user/users-list/users-list.component';
import { UiButtonComponent } from '@app/shared/ui/button/ui-button.component';

import { PaginationComponent } from '@app/shared/components/pagination/pagination.component';
import { ActiveCompanyService } from '@app/shared/services/active-company.service';
import { DialogService } from '@app/shared/services/shared/dialog.service';
import { UsersDataService } from '@app/shared/services/users-data.service';
import { SortDirection } from '@app/shared/types/enums/sort-direction.enum';
import { User } from '@app/shared/types/models/user/user.model';
import { UiInputComponent } from '@app/shared/ui/input/ui-input.component';
import { UiPageWrapperComponent } from '@app/shared/ui/page-wrapper/ui-page-wrapper.component';
import { UiSpinnerDirective } from '@app/shared/ui/spinner/ui-spinner.directive';
import { TranslatePipe } from '@ngx-translate/core';

@Component({
  selector: 'proffeo-users',
  templateUrl: 'company-users-view.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    UiPageWrapperComponent,
    TranslatePipe,
    UiInputComponent,
    UsersListComponent,
    PaginationComponent,
    UiButtonComponent,
    UiSpinnerDirective
  ]
})
export class CompanyUsersViewComponent {
  private readonly dialogService = inject(DialogService);
  private readonly usersDataService = inject(UsersDataService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly activeCompanyService = inject(ActiveCompanyService);
  private readonly queryParams = computed(() => ({
    page: this.page(),
    limit: this.limit(),
    search: this.searchValue(),
    sortField: this.sortField(),
    sortDir: this.sortDir(),
    companyId: this.activeCompany()?.id
  }));

  protected readonly searchValue = signal<string | undefined>(undefined);
  protected readonly page = signal(0);
  protected readonly limit = signal(25);
  protected readonly sortDir = signal<SortDirection>(SortDirection.DESC);
  protected readonly sortField = signal('createdAt');
  protected readonly activeCompany = this.activeCompanyService.activeCompany;

  protected readonly companyUsersPageList = computed(() => {
    const companyId = this.activeCompany()?.id;
    if (!companyId) {
      return undefined;
    }

    const allUsers = this.usersDataService.getCompanyUsers(companyId)();
    if (!allUsers) {
      return undefined;
    }

    // Filter by search
    const search = this.searchValue();
    const filteredUsers = search ? allUsers.filter(user => user.matchesSearch(search)) : allUsers;

    // Sort users
    const sortField = this.sortField();
    const sortDir = this.sortDir();
    const sortedUsers = [...filteredUsers].sort((a, b) => {
      let aValue: unknown = a[sortField as keyof User];
      let bValue: unknown = b[sortField as keyof User];

      // Handle Date fields
      if (aValue instanceof Date && bValue instanceof Date) {
        aValue = aValue.getTime();
        bValue = bValue.getTime();
      }

      // Handle undefined/null values
      if (aValue === undefined || aValue === null) return 1;
      if (bValue === undefined || bValue === null) return -1;

      // Compare values
      const comparison = aValue > bValue ? 1 : aValue < bValue ? -1 : 0;
      return sortDir === SortDirection.ASC ? comparison : -comparison;
    });

    // Paginate
    const page = this.page();
    const limit = this.limit();
    const startIndex = page * limit;
    const endIndex = startIndex + limit;
    const paginatedUsers = sortedUsers.slice(startIndex, endIndex);

    return {
      content: paginatedUsers,
      total: sortedUsers.length
    };
  });
  public constructor() {
    effect(() => {
      const companyId = this.activeCompany()?.id;
      if (companyId) {
        this.usersDataService.loadCompanyUsers(companyId);
      }
    });
  }

  protected openAndHandleCreateUserDialog(): void {
    this.dialogService
      .openCreateUserDialog(this.queryParams())
      .afterClosed()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe();
  }

  protected deleteUser(user: User): void {
    this.usersDataService
      .deleteUser(this.queryParams().companyId, user)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({ next: () => {}, error: () => {} });
  }

  protected resendActivationLink(user: User): void {
    this.usersDataService
      .resendActivationLink(user)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({ next: () => {}, error: () => {} });
  }

  protected lockUser(user: User): void {
    this.usersDataService
      .lockUser(this.activeCompany()?.id, user)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({ next: () => {}, error: () => {} });
  }

  protected unlockUser(user: User): void {
    this.usersDataService
      .unlockUser(this.activeCompany()?.id, user)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({ next: () => {}, error: () => {} });
  }
}
